import { db } from "../firebaseClient"
import { doc, setDoc, updateDoc, deleteDoc, getDoc, collection, getDocs, query, where, serverTimestamp } from "firebase/firestore"
import { createEnrollment, assignCohort } from "./enrollmentHelpers"
import { sendLearnerInviteEmail } from "./emailHelpers"
import { logPlatformEvent } from "./eventHelpers"

const DAY_MS = 86400000

// Invitations are top-level (like certificates) rather than an org subcollection,
// because they need to be publicly readable by ID alone from an unauthenticated
// context — someone clicking a link before they've signed up. orgId is stored as
// a field for rules/queries instead of living in the path.
export const createInvitation = async ({
  orgId,
  orgName,
  invitedByName,
  type, // "course" | "cohort" | "program"
  email, // "" for a shareable link not tied to one person
  courseId,
  courseTitle,
  cohortId,
  cohortName,
  programId,
  programTitle,
  expiresInDays = 30,
}) => {
  if (!orgId || !type) {
    return { invitationId: null, inviteLink: null, error: { message: "Missing required invitation fields" } }
  }

  try {
    const ref = doc(collection(db, "invitations"))
    const invitationId = ref.id
    const cleanEmail = email?.trim()?.toLowerCase() || ""

    await setDoc(ref, {
      invitationId,
      orgId,
      orgName: orgName || "",
      invitedByName: invitedByName || "",
      type,
      email: cleanEmail,
      courseId: courseId || "",
      courseTitle: courseTitle || "",
      cohortId: cohortId || "",
      cohortName: cohortName || "",
      programId: programId || "",
      programTitle: programTitle || "",
      status: "sent",
      openedAt: null,
      acceptedAt: null,
      acceptedByUid: null,
      createdAt: serverTimestamp(),
      expiresAt: new Date(Date.now() + expiresInDays * DAY_MS).toISOString(),
    })

        const inviteLink = `${window.location.origin}/authentication-portal?invite=${invitationId}`

    logPlatformEvent({
      type: "INVITATION_SENT",
      orgId,
      targetPath: `invitations/${invitationId}`,
      metadata: { type, email: cleanEmail, courseId: courseId || "", programId: programId || "" },
    })

    if (cleanEmail) {
      sendLearnerInviteEmail({
        toEmail: cleanEmail,
        orgName,
        invitedByName,
        courseTitle: courseTitle || programTitle || "",
        inviteLink,
      }).catch(() => {})
    }

    return { invitationId, inviteLink, error: null }
  } catch (error) {
    return { invitationId: null, inviteLink: null, error }
  }
}

export const getInvitation = async (invitationId) => {
  try {
    const snap = await getDoc(doc(db, "invitations", invitationId))
    if (!snap.exists()) return { invitation: null, error: null }
    return { invitation: snap.data(), error: null }
  } catch (error) {
    return { invitation: null, error }
  }
}

// Best-effort: records the first time an invite link is actually opened,
// regardless of whether the visitor goes on to sign up. Only ever sets
// openedAt once (first-open wins) so repeat clicks don't skew tracking.
export const markInvitationOpened = async (invitationId) => {
  try {
    const ref = doc(db, "invitations", invitationId)
    const snap = await getDoc(ref)
    if (snap.exists() && !snap.data()?.openedAt) {
      await updateDoc(ref, { openedAt: new Date().toISOString() })
    }
  } catch (error) {
    // silent — tracking should never block the actual signup/login flow
  }
}

// Called right after a learner completes signup or login with a pending
// ?invite= in the URL. Mirrors linkPendingInvitesForUser's job for team
// invites, but enrolls into a course/cohort/program instead of linking org
// membership. Safe to call even if the invitation was already accepted or
// has expired — just no-ops.
export const acceptInvitation = async (invitationId, uid) => {
  if (!invitationId || !uid) return { error: null }

  try {
    const ref = doc(db, "invitations", invitationId)
    const snap = await getDoc(ref)
    if (!snap.exists()) return { error: null }

    const invitation = snap.data()
    if (invitation?.status === "accepted") return { error: null }
    if (invitation?.expiresAt && new Date(invitation.expiresAt).getTime() < Date.now()) return { error: null }

    if (invitation?.type === "program" && invitation?.programId) {
      // Program invites enroll into every bundled course — read live from the
      // program doc rather than trusting a possibly-stale courseIds snapshot
      // taken at invite-creation time.
      const { listPrograms } = await import("./programHelpers")
      const { programs } = await listPrograms(invitation.orgId)
      const program = programs?.find((p) => p?.programId === invitation.programId)
      const courseIds = program?.courseIds || []
      await Promise.all(
        courseIds.map((courseId) =>
          createEnrollment({ userId: uid, courseId, orgId: invitation.orgId, enrollmentType: "invite" })
        )
      )
    } else if (invitation?.courseId) {
      await createEnrollment({
        userId: uid,
        courseId: invitation.courseId,
        orgId: invitation.orgId,
        enrollmentType: "invite",
      })
      if (invitation?.cohortId) {
        await assignCohort(uid, invitation.courseId, { cohortId: invitation.cohortId, cohortName: invitation.cohortName })
      }
    }

        await updateDoc(ref, { status: "accepted", acceptedAt: new Date().toISOString(), acceptedByUid: uid })
    logPlatformEvent({
      type: "INVITATION_ACCEPTED",
      actorUid: uid,
      orgId: invitation?.orgId,
      targetPath: `invitations/${invitationId}`,
      metadata: { courseId: invitation?.courseId || "", programId: invitation?.programId || "" },
    })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

export const listOrgInvitations = async (orgId) => {
  try {
    const invitationsRef = collection(db, "invitations")
    const q = query(invitationsRef, where("orgId", "==", orgId))
    const snap = await getDocs(q)
    const invitations = snap.docs?.map((d) => d.data())
    invitations.sort((a, b) => (b?.expiresAt || "").localeCompare(a?.expiresAt || ""))
    return { invitations, error: null }
  } catch (error) {
    return { invitations: [], error }
  }
}

export const revokeInvitation = async (invitationId) => {
  try {
    await deleteDoc(doc(db, "invitations", invitationId))
    logPlatformEvent({
      type: "INVITATION_REVOKED",
      targetPath: `invitations/${invitationId}`,
      metadata: {},
    })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

// Derives display status without mutating the stored doc — "expired" is
// computed at read time from expiresAt rather than a background job (no
// Cloud Functions on Spark plan to run one).
export const getInvitationDisplayStatus = (invitation) => {
  if (invitation?.status === "accepted") return "accepted"
  const expired = invitation?.expiresAt && new Date(invitation.expiresAt).getTime() < Date.now()
  if (expired) return "expired"
  if (invitation?.openedAt) return "opened"
  return "pending"
}

export const computeInvitationMetrics = (invitations) => {
  const total = invitations?.length || 0
  const accepted = invitations?.filter((i) => getInvitationDisplayStatus(i) === "accepted")?.length || 0
  const opened = invitations?.filter((i) => !!i?.openedAt && getInvitationDisplayStatus(i) !== "accepted")?.length || 0
  const expired = invitations?.filter((i) => getInvitationDisplayStatus(i) === "expired")?.length || 0
  const pending = invitations?.filter((i) => getInvitationDisplayStatus(i) === "pending")?.length || 0
  return { sent: total, opened, accepted, pending, expired }
}